import React, { FC, useMemo } from 'react'
import { cloneDeep } from 'lodash'

import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table'

import { AddBreak } from './components/AddBreak'
import { DraggableClassRow } from './components/DraggableClassRow'
import { OrderOfGoTableHeader } from './components/OrderOfGoTableHeader'

import FirestoreService from '../../../../../../services/firestoreService'

import useToasterHelper from '../../../../../../helpers/ToasterHelper'
import { useEventRiderTeamMembers } from '../../../../../../hooks/useEventRiderTeamMembers'
import { getClassColumns } from './data/columns-class'
import { getRidersByRideTimeByClass } from './data/order-of-go-rider-utils'
import { getScratchItems } from '../../../../../../helpers/scratch'
import { httpService } from '../../../../../../services/httpService'

import { selectedEvent, setSelectedEventKey } from '../../../../../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'

import { RegistrationByDayModel } from '../../../../../../models/registrations-by-day/registrationByDay.model'
import { RegistrationFeesType } from '../../../../../../models/event-fees/event-fees.interface'
import { IBreak, IBreakRegistrationByDay } from '../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IManageOption } from '../../types/IManageOption'
import { IScratchPayload } from '../../../../../../types/payment'

import { CONST } from '../../../../../../const/const'
import { MESSAGES_CONST } from '../../../../../../const/messages-const'

interface OrderOfGoTableByClassProps {
  eventId: string
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  registrationsByDay: IRegistrationByDayInterface[] | null
  fee: RegistrationFeesType | null
  increment: string
  breaks: IBreak[]
  setBreaks: (value: IBreak[]) => void
  registrationsByDayByClass: IRegistrationByDayInterface[]
  setRegistrationsByDayByClass: (value: IRegistrationByDayInterface[]) => void
  selectedClass: IManageOption | null
}

export const OrderOfGoTableByClass: FC<OrderOfGoTableByClassProps> = ({
  setRegistrationsByDayByClass,
  eventId,
  setRegistrationsByDay,
  registrationsByDay,
  fee,
  increment,
  registrationsByDayByClass,
  breaks,
  setBreaks,
  selectedClass,
}) => {
  const toastFunctions = useToasterHelper()
  const { eventTeamMembers, eventTeamMembersLoading } = useEventRiderTeamMembers(eventId)
  const dispatch = useAppDispatch()

  const currentEvent = useAppSelector(selectedEvent)

  const registrationsToDisplay: IBreakRegistrationByDay[] = useMemo(() => {
    return getRidersByRideTimeByClass(
      [
        ...registrationsByDayByClass,
        ...breaks?.filter((item) => item.registrationByDayUuid === fee?.uuid),
      ],
      fee,
      increment
    )
  }, [registrationsByDayByClass, breaks, increment, fee])

  const updateBreaks = async (breaks: IBreak[], eventId: string) => {
    dispatch(
      setSelectedEventKey({
        key: 'Event',
        value: { ...currentEvent.Event, breaks },
      })
    )
    await FirestoreService.updateItem(
      CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.EVENTS.NAME,
      eventId,
      {
        breaks,
      }
    )
  }

  const onOrderOfGoScratched = async (row: IBreakRegistrationByDay, state: boolean) => {
    try {
      let newData = registrationsToDisplay.map((registration) =>
        registration.id === row.id ? { ...registration, orderOfGoScratched: state } : registration
      )
      const breaksToUpdate: IBreak[] = []
      const riders: IRegistrationByDayInterface[] = []

      let count = 0
      newData = newData.map((registration) => {
        if (registration.orderOfGoScratched) {
          return { ...registration, order: 0 }
        } else {
          if (!registration.isOutOfTurn) count++
          return { ...registration, order: count }
        }
      })

      newData.forEach((item) => {
        if (item.isBreakDrag && !item.orderOfGoScratched) {
          breaksToUpdate.push(item as IBreak)
        }
        if (!item.isBreakDrag) {
          riders.push(item as IRegistrationByDayInterface)
        }
      })

      const newRegistrationsByDay =
        registrationsByDay?.map((registrationByDay) => {
          const newRegistrationByDay = newData.find(
            (current) => current.id === registrationByDay.id
          )
          if (newRegistrationByDay) {
            return newRegistrationByDay as IRegistrationByDayInterface
          } else {
            return registrationByDay as IRegistrationByDayInterface
          }
        }) ?? []

      setRegistrationsByDayByClass([...riders])
      setBreaks([...breaksToUpdate])
      setRegistrationsByDay([...newRegistrationsByDay])
      await updateBreaks(breaksToUpdate, eventId)

      // Update firestore collection on BE by batch
      if (riders?.[0]) {
        await httpService({
          url: 'update_registrations_by_day',
          method: 'POST',
          data: {
            registrationsByDay: riders,
          },
        })
      }

      toastFunctions.success({ message: 'Dropped Successfully' })
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const onScratchByClass = async (row: IRegistrationByDayInterface) => {
    try {
      if (row.registrationDocId) {
        const { scratchedRegistrations } = getScratchItems({
          registrationsByDay: [row],
          fees: [],
          tickets: [],
          chargeSalesTax: currentEvent?.EventPaymentSettings?.chargeSalesTax ?? false,
        })

        const payloadData: IScratchPayload = {
          registrationDocId: row.registrationDocId,
          scratchedRegistrationByDayItems: scratchedRegistrations.scratchedItems,
          scratchedFeesItems: [],
          scratchedTicketItems: [],
          isChargeSalesTax: currentEvent?.EventPaymentSettings?.chargeSalesTax ?? false,
          isAlreadyScratched: false,
        }

        await httpService({
          url: 'scratch_items',
          method: 'POST',
          data: payloadData,
        })

        const registrationsByDayToUpdate =
          registrationsByDay?.map((item) =>
            item.id === row.id ? { ...item, isScratched: true } : item
          ) || []

        const registrationsByDayByClassToUpdate =
          registrationsByDayByClass?.map((item) =>
            item.id === row.id ? { ...item, isScratched: true } : item
          ) || []

        setRegistrationsByDayByClass([...registrationsByDayByClassToUpdate])
        setRegistrationsByDay([...registrationsByDayToUpdate])

        toastFunctions.success({ message: 'Scratched Successfully' })
      }
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const onRemoveBreak = async (row: IBreakRegistrationByDay) => {
    try {
      const newBreaks = breaks.filter((item) => item.id !== row.id)
      await updateBreaks(newBreaks, eventId)
      setBreaks([...newBreaks])
      toastFunctions.success({ message: 'Break removed successfully' })
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const onDragEnd = async (event: DragEndEvent) => {
    try {
      const { active, over } = event
      let newArray: IBreakRegistrationByDay[] = []

      if (active && over && active.id !== over.id) {
        const oldIndex = registrationsToDisplay?.map(({ id }) => id).indexOf(String(active.id))
        const newIndex = registrationsToDisplay?.map(({ id }) => id).indexOf(String(over.id))
        newArray = arrayMove(registrationsToDisplay, oldIndex, newIndex)

        let count = 0
        newArray = newArray.map((item) => {
          if (item.orderOfGoScratched) {
            return { ...item, order: 0 }
          } else {
            if (!item.isOutOfTurn) count++
            return { ...item, order: count }
          }
        })

        if (oldIndex !== -1 && newIndex !== -1) {
          const oldItem = newArray[oldIndex]
          const newItem = newArray[newIndex]

          //  Swap rideTime only between valid registrations (not breaks)
          if (!oldItem.isBreakDrag && !newItem.isBreakDrag) {
            let tempRideTime = oldItem.rideTime
            newArray[oldIndex].rideTime = newItem.rideTime
            newArray[newIndex].rideTime = tempRideTime
          }
        }

        const breaksToUpdate: IBreak[] = []
        const riders: IRegistrationByDayInterface[] = []

        newArray.forEach((item) => {
          if (item.isBreakDrag) {
            breaksToUpdate.push(item as IBreak)
          }
          if (!item.isBreakDrag) {
            riders.push(item as IRegistrationByDayInterface)
          }
        })

        const newRegistrationsByDay =
          registrationsByDay?.map((registrationByDay) => {
            const newRegistrationByDay = newArray.find(
              (current) => current.id === registrationByDay.id
            )
            if (newRegistrationByDay) {
              return newRegistrationByDay as IRegistrationByDayInterface
            } else {
              return registrationByDay as IRegistrationByDayInterface
            }
          }) ?? []

        setRegistrationsByDayByClass([...riders])
        setBreaks([...breaksToUpdate])
        setRegistrationsByDay(newRegistrationsByDay)
        await updateBreaks(breaksToUpdate, eventId)

        const updatedPromises = riders.map(async (item) => {
          const newData = new RegistrationByDayModel(item)

          return FirestoreService.updateItem(
            CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.REGISTRATION_BY_DAY.NAME,
            newData.id,
            newData.toFirestore()
          )
        })

        await Promise.all(updatedPromises)
      }

      toastFunctions.success({ message: MESSAGES_CONST.EVENT_ORDER_UPDATED })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const saveHandlerBreak = async (h: string, m: string, a: string, breakId: string) => {
    const newBreakTime = `${h}:${m}:${a}`
    const newData = cloneDeep(breaks)
    const breakIndex = newData.findIndex((e) => e.id === breakId)

    if (breakIndex < 0) {
      toastFunctions.error({ message: `Break with id ${breakId} not found in staged data` })
      return
    }

    newData[breakIndex] = {
      ...newData[breakIndex],
      time: newBreakTime,
    }

    try {
      await updateBreaks(newData, eventId)
      setBreaks([...newData])
      toastFunctions.success({ message: MESSAGES_CONST.EVENT_ORDER_UPDATED })
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const columns = useMemo<ColumnDef<IBreakRegistrationByDay>[]>(() => {
    return getClassColumns({
      eventTeamMembers,
      eventTeamMembersLoading,
      saveHandlerBreak,
    })
  }, [eventTeamMembers, eventTeamMembersLoading, breaks])

  const table = useReactTable({
    data: registrationsToDisplay,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id, // required because row indexes will change
  })

  const addBreak = async () => {
    try {
      if (!selectedClass?.value) return

      const dataToCreate = {
        id: `${new Date().getTime()}`,
        order: registrationsByDayByClass.length + 1,
        time: '00:00:00',
        isBreakDrag: true,
        orderOfGoScratched: false,
        registrationByDayName: selectedClass.label,
        registrationByDayUuid: selectedClass.value,
      }

      await updateBreaks([...breaks, dataToCreate], eventId)
      const newData = [...breaks, dataToCreate]

      setBreaks([...newData])
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  return registrationsToDisplay.length > 0 ? (
    <DndContext key={registrationsToDisplay.length} onDragEnd={onDragEnd}>
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup, index) => (
            <OrderOfGoTableHeader key={index} headerGroup={headerGroup} />
          ))}
        </thead>
        <tbody>
          <SortableContext
            items={registrationsToDisplay?.map(({ id }) => id)}
            strategy={verticalListSortingStrategy}
          >
            {registrationsToDisplay.map((row, rowIndex) => (
              <DraggableClassRow
                key={row.id}
                row={row}
                tableRow={table.getRowModel().rows[rowIndex]}
                rowIndex={rowIndex}
                isLastChild={rowIndex === registrationsToDisplay.length - 1}
                registrationsToDisplay={registrationsToDisplay}
                onOrderOfGoScratched={onOrderOfGoScratched}
                onScratch={onScratchByClass}
                onRemoveBreak={onRemoveBreak}
              />
            ))}
          </SortableContext>
          <AddBreak addBreak={addBreak} />
        </tbody>
      </table>
    </DndContext>
  ) : (
    <div className="flex justify-center p-6">There are no records to display</div>
  )
}
